import { useEffect, useState } from "react"
import Selector from "@/v2-backoffice-client/components/selector"
import type { TranslationCategory } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAddRowTypeSetSelectorProps = {
  categories: TranslationCategory[]
  value: string
  onChange(key: string): void
  disabled: boolean
  placeholder: string
}

// Searchable type-set combobox for the "Add text" dialog, split out of
// TranslationAddRowFields as its own file per this codebase's XxxSelector.tsx
// convention (LayerCategoryLayerAddSelector.tsx, InvitationTypeSelector.tsx).
// Owns its own open state — the parent never needs to force it closed except
// when it becomes irrelevant/inert (Kind switched away from "Data label", or
// a create request is in flight), which the effect below handles directly.
function TranslationAddRowTypeSetSelector(
  props: TranslationAddRowTypeSetSelectorProps,
) {
  const { categories, value, onChange, disabled, placeholder } = props
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  return (
    <Selector open={open} onOpenChange={setOpen} shouldFilter>
      <Selector.Trigger
        id="add-row-typeset"
        variant="outline"
        size="md"
        className="w-full font-normal"
        align="between"
        iconSrc="angle-small-down"
        iconPosition="right"
        disabled={disabled}
      >
        {value || placeholder}
      </Selector.Trigger>
      <Selector.Content>
        <Selector.Search placeholder="Search type sets..." debounce={0} />
        <Selector.List emptyLabel="No type sets found">
          {categories.map((candidate) => (
            <Selector.Item
              key={candidate.key}
              value={candidate.key}
              added={value === candidate.key}
              onSelect={() => {
                onChange(candidate.key)
                setOpen(false)
              }}
            >
              <Selector.ItemText primary={candidate.key} />
            </Selector.Item>
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default TranslationAddRowTypeSetSelector
